import { LightningBoltIcon } from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import { TaskStatus } from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import type { TaskPerson } from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import "components/Dashboard/PeoplePanel/index.css";
import { formatDate } from "components/Forms/ResponseDialog";
import { namesInWords } from "components/MessageTemplates/TemplatePreviewParts";
import PersonPill, { PillColor } from "components/PersonPill";
import {
  ButtonHTMLAttributes,
  forwardRef,
  Fragment,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useAutomations, useReference } from "./hooks";
import { statusLabel } from "./kinds";
import {
  columnById,
  nameOf,
  parsePersonKey,
  personKey,
  ReferenceData,
} from "./reference";
import type {
  ActivityAction,
  ActivityDetails,
  ApprovalItem,
  Approvers,
  ClosedTask,
  MessageChannel,
  PersonKey,
  PersonRole,
  Proposal,
  SentMessage,
  Step,
} from "./types";
import {
  APPROVER_WORDS,
  capitalize,
  channelWords,
  subjectsWords,
} from "./words";

/*
 * Pieces the Activity tab's two lists share: people as pills, the link to
 * an automation, the table, a row's preview and what its pop-up shows, and
 * the words for a card's title, who was asked and who can approve.
 */

// ── People ────────────────────────────────────────────────────────────────

/** People's pill colors, as on tasks: admins gray, then by role. */
const ROLE_COLORS: Record<PersonRole, PillColor> = {
  admin: "gray",
  tutor: "gold",
  parent: "blue",
  student: "green",
};

/**
 * A person as a pill, in their role's color. Unless it can't (opens), it
 * opens the person's page in a new tab, as the pills on tasks do (admins
 * have no page).
 */
export function PersonLinkPill({
  person,
  label,
  title,
  opens = true,
}: {
  person: PersonKey;
  label: string;
  title?: string;
  opens?: boolean;
}) {
  const { role, id } = parsePersonKey(person);
  return (
    <PersonPill
      label={label}
      title={title}
      color={ROLE_COLORS[role] ?? "gray"}
      onClick={
        opens && role !== "admin"
          ? () => window.open(`/manage/${role}s/${id}`, "_blank")
          : undefined
      }
    />
  );
}

/** People as pills, each opening their page. */
export function PeoplePills({ people }: { people: PersonKey[] }) {
  const ref = useReference();
  if (!people.length) return <span className="text-gray-400">Nobody</span>;
  return (
    <span className="inline-flex min-w-0 flex-wrap gap-1 align-middle">
      {people.map((key) => (
        <PersonLinkPill
          key={key}
          person={key}
          label={nameOf(ref, key)}
          opens={!!ref.personByKey[key]}
        />
      ))}
    </span>
  );
}

/** A person as the task form's people pickers take them. */
export function taskPerson(
  ref: ReferenceData,
  key: PersonKey
): TaskPerson | null {
  const person = ref.personByKey[key];
  if (!person) return null;
  return person.role === "admin"
    ? { role: "admin", id: person.id, label: person.name }
    : {
        role: person.role as PersonKind,
        id: Number(person.id),
        label: person.name,
      };
}

export function taskPeopleOf(ref: ReferenceData, keys: PersonKey[]) {
  return keys
    .map((key) => taskPerson(ref, key))
    .filter((person): person is TaskPerson => person !== null);
}

export function keyOfTaskPerson(person: TaskPerson): PersonKey {
  return personKey(person.role as PersonRole, person.id);
}

/** Everyone a task or message can name, for the people pickers. */
export function useTaskPeople() {
  const ref = useReference();
  return useMemo(
    () =>
      taskPeopleOf(
        ref,
        ref.people.map((person) => person.key)
      ),
    [ref]
  );
}

// ── Automations ───────────────────────────────────────────────────────────

/** The automations by id: their names now, their steps, and whether they are still there. */
export function useAutomationIndex() {
  const { automations, loading } = useAutomations();
  return useMemo(() => {
    const byId = new Map(automations.map((a) => [a.id, a]));
    return {
      /** Its name now; as it was, once it is deleted. */
      name: (id: string | null, was: string) =>
        (id && byId.get(id)?.name) || was,
      /** Still there (assumed so while the list loads). */
      live: (id: string | null) => !!id && (loading || byId.has(id)),
      automation: (id: string | null) => (id ? byId.get(id) ?? null : null),
      step: (id: string | null, stepId: string | null): Step | null =>
        (id && byId.get(id)?.steps.find((s) => s.id === stepId)) || null,
    };
  }, [automations, loading]);
}

/** Where an automation opens: its lane on the Automations tab, with its editor. */
export function automationHref(id: string) {
  return `/automations?open=${encodeURIComponent(id)}`;
}

/**
 * An automation's name, linking to it on the Automations tab; plain gray
 * once it is deleted, and plain for a tutor (the page is for admins).
 */
export function AutomationLink({
  id,
  name,
  live,
}: {
  id: string | null;
  name: string;
  live: boolean;
}) {
  const ref = useReference();
  if (!id || !live) return <span className="text-gray-500">{name}</span>;
  if (ref.me && parsePersonKey(ref.me).role === "tutor")
    return <span className="font-medium text-gray-900">{name}</span>;
  return (
    <Link
      to={automationHref(id)}
      className="font-medium text-gray-900 hover:underline"
    >
      {name}
    </Link>
  );
}

/**
 * An automation's name as a purple pill, the look of tasks' Automatic
 * chip, opening it on the Automations tab in a new tab, to look at (its
 * editor stays closed). Gray once it is deleted, and not a link for a
 * tutor (the page is for admins).
 */
export function AutomationPill({
  id,
  name,
  live,
}: {
  id: string | null;
  name: string;
  live: boolean;
}) {
  const ref = useReference();
  const content = (
    <>
      <LightningBoltIcon
        className="-mt-px mr-0.5 inline h-2.5 w-2.5"
        aria-hidden
      />
      {name}
    </>
  );
  if (!id || !live)
    return (
      <span className="person-pill person-pill--gray" title={name}>
        {content}
      </span>
    );
  if (ref.me && parsePersonKey(ref.me).role === "tutor")
    return (
      <span className="person-pill person-pill--purple" title={name}>
        {content}
      </span>
    );
  return (
    <a
      href={automationHref(id)}
      target="_blank"
      rel="noreferrer"
      // Plain like the other pills: the app's links underline on hover.
      className="person-pill person-pill--purple person-pill--button no-underline hover:no-underline focus:no-underline"
      title={name}
    >
      {content}
    </a>
  );
}

// ── The table ─────────────────────────────────────────────────────────────

/** A table cell's room. */
export const CELL = "px-3 py-3";

/** The columns, each its own width; Preview takes the room left. */
const COLUMNS = [
  { label: "Status", width: "w-28" },
  { label: "Action", width: "w-32" },
  { label: "Preview", width: "" },
  { label: "About", width: "w-44" },
  { label: "Automation", width: "w-56" },
  { label: "When", width: "w-48" },
];

/**
 * Activity's table, the same for both lists: Status, Action, Preview,
 * About, Automation and When across the page's width, and for Waiting for
 * approval the answers at the right (answers). faded: another page or
 * filter is loading.
 */
export function ActivityTable({
  answers = false,
  faded = false,
  children,
}: {
  answers?: boolean;
  faded?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`relative mt-4 overflow-x-auto transition-opacity ${
        faded ? "opacity-50" : ""
      }`}
    >
      <table
        className={`w-full table-fixed border-collapse rounded-lg bg-white ${
          answers ? "min-w-[60rem]" : "min-w-[50rem]"
        }`}
      >
        <thead>
          <tr className="border-b-2 border-gray-300">
            {COLUMNS.map((column) => (
              <th
                key={column.label}
                scope="col"
                className={`whitespace-nowrap p-3 text-left text-sm font-bold ${column.width}`}
              >
                {column.label}
              </th>
            ))}
            {answers && (
              <th scope="col" className="w-44 p-3">
                <span className="sr-only">Approve or skip</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** A row's preview as a button: one line, cut to fit, that opens its pop-up. */
export const PreviewButton = forwardRef<
  HTMLButtonElement,
  { children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>
>(function PreviewButton({ children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className="block w-full min-w-0 truncate text-left text-sm text-gray-700 hover:text-gray-900"
      {...props}
    >
      {children}
    </button>
  );
});

/** Nothing (an empty field, no column), in gray. */
export const NONE = <span className="text-gray-400">None</span>;

/** A closed task's name once it is deleted. */
const DELETED_TASK = "A task that was deleted";

/** What an action's preview is made from: a row's details, its words, and the org's data. */
interface PreviewSource {
  details: ActivityDetails;
  /** Its words (a row's title), when the details say nothing more. */
  title: string;
  ref: ReferenceData;
}

/** An action's preview: its line in the Preview column, and its part of the pop-up. */
interface ActionPreview {
  /** One line, cut to fit; null: only its title. */
  line: (source: PreviewSource) => ReactNode | null;
  /** The pop-up's facts, each a label and its value. */
  facts: (source: PreviewSource) => [string, ReactNode][];
  /** What the pop-up shows under them: a message's wording, each field before and after. */
  more?: (source: PreviewSource) => ReactNode;
}

/** Who a row's tasks went (or would go) to, each once. */
function assigneesOf(details: ActivityDetails): PersonKey[] {
  return Array.from(new Set(details.tasks.flatMap((task) => task.assignees)));
}

/**
 * Each kind of action's preview, in one place, for Done (what it did) and
 * Waiting for approval (what it would do): a message's subject and the
 * start of its body, To, By and its wording; a task's name, people and due
 * date; a move's From and To; each field's before and after. A new kind of
 * action adds its entry here.
 */
const PREVIEWS: Record<ActivityAction, ActionPreview> = {
  message: {
    line: ({ details }) => {
      const first = details.message?.sent[0];
      if (!first) return null;
      const start = first.body.replace(/\s+/g, " ").trim();
      if (!first.subject) return start;
      return (
        <>
          <span className="font-medium text-gray-900">{first.subject}</span>{" "}
          <span className="text-gray-500">{start}</span>
        </>
      );
    },
    facts: ({ details }) => {
      const channels = details.message?.channels ?? [];
      const facts: [string, ReactNode][] = [
        ["To", <PeoplePills people={details.message?.to ?? []} />],
      ];
      if (channels.length) facts.push(["By", byWords(channels)]);
      return facts;
    },
    more: ({ details }) =>
      details.message?.sent.length ? (
        <Wording sent={details.message.sent} />
      ) : null,
  },
  task: {
    line: ({ details, title, ref }) => {
      if (!details.tasks.length) return null;
      const name = details.tasks.find((task) => task.name)?.name ?? title;
      const due = details.tasks.find((task) => task.dueDate)?.dueDate;
      const more = [
        namesInWords(assigneesOf(details).map((key) => nameOf(ref, key))),
        due ? `Due ${formatDate(due)}` : "",
      ].filter(Boolean);
      return (
        <>
          <span className="font-medium text-gray-900">{name}</span>
          {more.length > 0 && (
            <span className="text-gray-500"> · {more.join(" · ")}</span>
          )}
        </>
      );
    },
    facts: ({ details }) => {
      const facts: [string, ReactNode][] = [];
      const people = assigneesOf(details);
      if (people.length)
        facts.push(["Assigned to", <PeoplePills people={people} />]);
      const due = details.tasks.find((task) => task.dueDate)?.dueDate;
      if (due) facts.push(["Due", formatDate(due)]);
      return facts;
    },
  },
  crm_move: {
    line: ({ details }) => {
      const move = details.move;
      if (!move?.to) return null;
      if (move.already) return <>Already in {move.to}</>;
      if (move.from === null) return null;
      return (
        <>
          {move.from.length ? move.from.join(", ") : NONE} → {move.to}
        </>
      );
    },
    facts: ({ details }) => {
      const facts: [string, ReactNode][] = [];
      const move = details.move;
      if (move?.from)
        facts.push(["From", move.from.length ? move.from.join(", ") : NONE]);
      if (move?.to) facts.push(["To", move.to]);
      return facts;
    },
  },
  crm_field: {
    line: ({ details }) =>
      details.fields.length ? (
        <>
          {details.fields.map((field, n) => (
            <Fragment key={`${field.label}:${n}`}>
              {n > 0 && "; "}
              {field.label}: {field.before ?? NONE} → {field.after ?? NONE}
            </Fragment>
          ))}
        </>
      ) : null,
    facts: () => [],
    more: ({ details }) => <Changes fields={details.fields} />,
  },
  close_task: {
    line: ({ details }) => {
      const tasks = details.close?.tasks ?? [];
      if (!tasks.length) return null;
      const first = tasks[0];
      return (
        <>
          <span className="font-medium text-gray-900">
            {first.name ?? DELETED_TASK}
            {tasks.length > 1 && ` and ${tasks.length - 1} more`}
          </span>
          <span className="text-gray-500">
            {" "}
            · {statusLabel(first.before)} → {statusLabel(first.after)}
          </span>
        </>
      );
    },
    facts: ({ details }) => {
      const close = details.close;
      if (!close) return [];
      const facts: [string, ReactNode][] = [];
      const names = (tasks: ClosedTask[]) =>
        namesInWords(tasks.map((task) => task.name ?? DELETED_TASK));
      if (close.tasks.length)
        facts.push([
          close.tasks.length > 1 ? "Tasks" : "Task",
          names(close.tasks),
        ]);
      const first = close.tasks[0];
      if (first)
        facts.push([
          "Status",
          `${statusLabel(first.before)} → ${statusLabel(first.after)}`,
        ]);
      const people = Array.from(
        new Set(close.tasks.flatMap((task) => task.assignees))
      );
      if (people.length)
        facts.push(["Assigned to", <PeoplePills people={people} />]);
      if (close.already.length) facts.push(["Already", names(close.already)]);
      return facts;
    },
  },
  assign_tutor: {
    line: ({ details, ref }) => {
      const given = (details.tutors?.students ?? []).filter(
        (s) => s.assigned.length
      );
      if (!given.length) return null;
      return given
        .map(
          (s) =>
            `${namesInWords(
              s.assigned.map((key) => nameOf(ref, key))
            )} to ${nameOf(ref, s.student)}`
        )
        .join("; ");
    },
    facts: ({ details, ref }) => {
      const students = details.tutors?.students ?? [];
      if (students.length === 1) {
        const [one] = students;
        const facts: [string, ReactNode][] = [
          ["Student", <PeoplePills people={[one.student]} />],
        ];
        if (one.assigned.length)
          facts.push([
            one.assigned.length > 1 ? "Tutors" : "Tutor",
            <PeoplePills people={one.assigned} />,
          ]);
        if (one.already.length)
          facts.push(["Already has", <PeoplePills people={one.already} />]);
        return facts;
      }
      // A parent's students, a line each.
      return students.map((s): [string, ReactNode] => [
        nameOf(ref, s.student),
        <>
          {s.assigned.length > 0 && <PeoplePills people={s.assigned} />}
          {s.already.length > 0 && (
            <span className="block text-gray-500">
              Already has{" "}
              {namesInWords(s.already.map((key) => nameOf(ref, key)))}
            </span>
          )}
        </>,
      ]);
    },
  },
  add_note: {
    line: ({ details }) =>
      details.addedNote?.text
        ? details.addedNote.text.replace(/\s+/g, " ").trim()
        : null,
    facts: ({ details }) => {
      const note = details.addedNote;
      if (!note) return [];
      const facts: [string, ReactNode][] = [];
      if (note.about)
        facts.push(["Record", <PeoplePills people={[note.about]} />]);
      if (note.author)
        facts.push(["Written by", <PeoplePills people={[note.author]} />]);
      if (note.pinned) facts.push(["Pinned", "Yes"]);
      return facts;
    },
    more: ({ details }) =>
      details.addedNote?.text ? (
        <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-gray-200 px-3 py-2 text-gray-700">
          {details.addedNote.text}
        </p>
      ) : null,
  },
  wait: {
    line: () => null,
    facts: () => [],
  },
};

/** A row's Preview column: what it did or would do, in one line (its title when there is nothing more to say). */
export function PreviewLine({
  action,
  details,
  title,
}: {
  action: ActivityAction;
  details: ActivityDetails;
  title: string;
}) {
  const ref = useReference();
  return <>{PREVIEWS[action].line({ details, title, ref }) ?? title}</>;
}

/** An action's part of a row's pop-up: its facts (and the view's own, after them), then what goes under them. */
export function PreviewParts({
  action,
  details,
  title,
  extra = [],
}: {
  action: ActivityAction;
  details: ActivityDetails;
  title: string;
  /** The view's own facts, after the action's. */
  extra?: [string, ReactNode][];
}) {
  const ref = useReference();
  const preview = PREVIEWS[action];
  const parts = { details, title, ref };
  return (
    <>
      <Facts facts={[...preview.facts(parts), ...extra]} />
      {preview.more?.(parts)}
    </>
  );
}

/**
 * A row's pop-up, opened from its trigger (its Action pill or its
 * preview): wider for a message's wording. children gets close.
 */
export function DetailsPopover({
  label,
  wide,
  trigger,
  children,
}: {
  label: string;
  wide: boolean;
  trigger: ReactElement;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        {/* As tall as the room on its side at most, scrolling past that, so it stays in the window. */}
        <Popover.Content
          className={`people-panel__menu ${
            wide ? "w-96" : "w-80"
          } max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-2rem)] overflow-y-auto`}
          align="start"
          sideOffset={4}
          collisionPadding={12}
          aria-label={label}
        >
          {children(() => setOpen(false))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A pop-up's facts, each a label and its value, as the approval cards set them out. */
export function Facts({ facts }: { facts: [string, ReactNode][] }) {
  if (!facts.length) return null;
  return (
    <dl className="m-0 grid grid-cols-[5.5rem,1fr] gap-x-3 gap-y-2 rounded-lg bg-gray-50 px-3 py-2.5">
      {facts.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="font-normal text-gray-500">{label}</dt>
          <dd className="m-0 min-w-0 break-words text-gray-900">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

const CHANNEL_LABELS: Record<MessageChannel, string> = {
  email: "Email",
  sms: "Text",
};

/** The ways a message goes, as a fact: "Email and text". */
export function byWords(channels: MessageChannel[]) {
  return capitalize(
    channelWords({
      email: channels.includes("email"),
      sms: channels.includes("sms"),
    }).replace(/^by /, "")
  );
}

/** A message's wording: the email's subject over its body, then the text. A long body scrolls. */
export function Wording({ sent }: { sent: SentMessage[] }) {
  return (
    <div className="space-y-2">
      {sent.map((message) => (
        <div key={message.channel}>
          {sent.length > 1 && (
            <p className="mb-1 text-xs font-medium text-gray-500">
              {CHANNEL_LABELS[message.channel]}
            </p>
          )}
          <div className="rounded-lg border border-gray-200">
            {message.subject && (
              <p className="break-words border-b border-gray-200 px-3 py-2 font-semibold text-gray-900">
                {message.subject}
              </p>
            )}
            <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words px-3 py-2 text-gray-700">
              {message.body}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Each field a row changed (or would change), before and after. */
export function Changes({ fields }: { fields: ActivityDetails["fields"] }) {
  if (!fields.length) return null;
  return (
    <ul className="m-0 list-none space-y-2 rounded-lg bg-gray-50 px-3 py-2.5">
      {fields.map((field, n) => (
        <li key={`${field.label}:${n}`} className="min-w-0 break-words">
          <span className="block text-gray-500">{field.label}</span>
          <span className="text-gray-900">
            {field.before ?? NONE} → {field.after ?? NONE}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Words ─────────────────────────────────────────────────────────────────

/** A card's title: the task, the template, the column it moves them to, or the field it sets. */
export function proposalTitle(ref: ReferenceData, proposal: Proposal) {
  switch (proposal.action) {
    case "task":
      return proposal.name;
    case "message":
      return proposal.templateName;
    case "crm_move":
      return `Move to ${
        columnById(ref, proposal.boardId, proposal.columnId)?.column.name ??
        proposal.columnLabel
      }`;
    case "crm_field":
      return proposal.mode
        ? subjectsWords(
            proposal.mode,
            proposal.valueLabel,
            `${nameOf(ref, proposal.person)}'s`
          )
        : `Set ${proposal.fieldLabel} to ${proposal.valueLabel}`;
    case "close_task": {
      if (proposal.would) return capitalize(proposal.would);
      const name = proposal.tasks[0]?.name;
      if (!name) return "Close a task";
      return proposal.status === TaskStatus.CANCELLED
        ? `Cancel “${name}”`
        : `Mark “${name}” done`;
    }
    case "assign_tutor":
      return proposal.would ? capitalize(proposal.would) : "Assign a tutor";
    case "add_note":
      return proposal.would ? capitalize(proposal.would) : "Add a note";
  }
}

/** Who a card is about: a task's About people, else the person it is about. */
export function aboutOf(item: ApprovalItem): PersonKey[] {
  const { proposal } = item;
  if (proposal.action === "task") return proposal.about;
  if (proposal.action === "crm_move" || proposal.action === "crm_field")
    return [proposal.person];
  return item.about ? [item.about] : [];
}

/** "you", "Rosa Diaz", "Rosa Diaz and all admins", "all admins", "Rosa Diaz and 4 others". */
export function askedWho(ref: ReferenceData, askedOf: PersonKey[]) {
  const everyAdmin =
    ref.admins.length > 1 &&
    ref.admins.every((admin) => askedOf.includes(admin));
  const others = askedOf.filter(
    (key) => !(everyAdmin && ref.admins.includes(key))
  );
  const names = [
    ...others.filter((key) => key === ref.me).map(() => "you"),
    ...others.filter((key) => key !== ref.me).map((key) => nameOf(ref, key)),
  ];
  const shown =
    names.length > 3 ? [`${names[0]} and ${names.length - 1} others`] : names;
  return (
    namesInWords([...shown, ...(everyAdmin ? ["all admins"] : [])]) ||
    "all admins"
  );
}

/** Who can approve an item, as its card's foot says it. */
export function approverWords(approvers: Approvers | null | undefined) {
  return APPROVER_WORDS[approvers ?? "admins"];
}

/** What started a run, as the backend says it: "When someone fills out Family onboarding", "3 days after a student's first meeting". */
export function causeWords(cause: string) {
  const text = cause.trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}
